import type { UseFormReturn } from 'react-hook-form';

import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import type { ActivityType } from '~/api/common/types';

import { useUser } from '~/api/user';
import { ExternalLink, Text, Title } from '~/components';
import { Field, Select } from '~/components/Form';
import { insertIf } from '~/utils/arrays';
import { isItDeveloper } from '~/utils/user';

import type { CreateEditProductFormType } from '../types';

import { ProductNameFields } from './ProductNameFields';
import { activityTypeDescriptionStyle, activityTypeLabelStyle, gridStyle, sectionStyle } from './productSections.css';

type Props = {
  form: UseFormReturn<CreateEditProductFormType>;
};

export const ProductBasicSection = ({ form }: Props) => {
  const { data: user } = useUser();
  const { t } = useTranslation(['lines', 'products', 'validation']);

  const { control } = form;

  const activityTypeItems = (
    [
      'CreativeGeneral',
      ...insertIf(isItDeveloper(user), 'CreativeComputer'),
      'NonCreative',
      'CreativeReuse',
      'CreativeSubcontracting',
    ] satisfies ActivityType[]
  ).map((activityType) => ({
    label: t(`lines:creativeWork.options.${activityType}.label`),
    description: t(`lines:creativeWork.options.${activityType}.description`),
    value: activityType,
  }));

  return (
    <div className={sectionStyle}>
      <Title as="h2" styleAs="h3">
        {t('products:createEdit.info.title')}
      </Title>

      <div className={gridStyle}>
        {/* Name (per language) */}
        <ProductNameFields form={form} />

        {/* Activity type */}
        <Controller
          control={control}
          name="activityType"
          render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label infotip={<Trans components={{ a: <ExternalLink /> }} i18nKey="products:fields.activityType.tooltip" />}>
                {t('products:fields.activityType.label')}
              </Field.Label>

              <Select.Root items={activityTypeItems} onValueChange={onChange} value={value}>
                <Select.Button placeholder={t('products:fields.activityType.placeholder')} />

                <Select.List>
                  {activityTypeItems.map((item) => (
                    <Select.Item key={item.value} value={item.value}>
                      <Text className={activityTypeLabelStyle}>{item.label}</Text>
                      <Text className={activityTypeDescriptionStyle}>{item.description}</Text>
                    </Select.Item>
                  ))}
                </Select.List>
              </Select.Root>

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{ required: { value: true, message: t('validation:required') } }}
        />
      </div>
    </div>
  );
};
